// =============================================================================
// domain/billing.ts — the four plans, and the arithmetic on them.
// -----------------------------------------------------------------------------
// Relocated from the old client's `BILLING_PLANS` / `_planAmount` /
// `founderAnnualSaving` (app/index.html:39857-39875). #65.
//
// ── 🚨 ONE SOURCE FOR A PRICE ──
//
// Every price the subscription card shows is derived from this object. The old
// client learned this the hard way and says so: the Founder saving is
// ARITHMETIC over the real prices "rather than a hardcoded $110 that silently
// lies the day a price changes".
//
// ⚠ Three price strings in the old client do NOT read this object — the beta
// banner, the AI-Coach upsell overlay and the Free column's "$0 /forever". They
// are known drift and are not reproduced here.
//
// ── 🔴 WHAT MUST NOT BE SAID ──
//
// No "lifetime price" claim, and no asterisk that walks one back. It was
// deliberately removed from the old app and a live assertion keeps it out.
// "Guaranteed lifetime price*" with a footnote retracting it is the same claim
// wearing a disclaimer — it reads as a tell to an audience that sees
// funded-account marketing all day, and pricing guarantees are exactly what a
// payment processor reviews.
//
// The true version is stronger anyway, because it is arithmetic: Founder is the
// annual Pro price minus the annual Founder price cheaper, every year you stay.
//
// ⚠ These four must match the live Stripe prices. They are display copy; the
// amount actually charged comes from the price ids held as Edge Function
// secrets, and nothing here can change what a customer pays.
// =============================================================================

export type PlanKey = 'founder_annual' | 'founder_monthly' | 'pro_annual' | 'pro_monthly';

export interface BillingPlan {
  label: string;
  /** As displayed, e.g. `$180/yr`. The amount is parsed back out of this. */
  price: string;
  /** The qualifying line under the price. Empty where there is nothing to add. */
  sub: string;
}

export const BILLING_PLANS: Record<PlanKey, BillingPlan> = {
  founder_annual: { label: 'Founder — Annual', price: '$180/yr', sub: 'held while subscribed' },
  founder_monthly: { label: 'Founder — Monthly', price: '$20/mo', sub: 'held while subscribed' },
  pro_annual: { label: 'Pro — Annual', price: '$290/yr', sub: '2 months free' },
  pro_monthly: { label: 'Pro — Monthly', price: '$29/mo', sub: '' },
};

/** `$180/yr` → 180. Zero for anything unparseable, so no caller renders NaN. */
export function planAmount(plan: BillingPlan | undefined): number {
  return Number(String(plan?.price ?? '').replace(/[^0-9.]/g, '')) || 0;
}

/** Just the money part of a price string: `$180/yr` → `$180`. */
export function planPriceOnly(plan: BillingPlan | undefined): string {
  return String(plan?.price ?? '').split('/')[0] ?? '';
}

/** What a Founder saves against standard Pro, per year, at the annual rate. */
export function founderAnnualSaving(): number {
  return planAmount(BILLING_PLANS.pro_annual) - planAmount(BILLING_PLANS.founder_annual);
}
